<template>
    <div v-if="available" class="ai-chat">
        <!-- Launcher -->
        <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
                <v-btn
                    fab
                    dark
                    width="56"
                    height="56"
                    class="ai-chat__launcher"
                    :class="{ 'ai-chat__launcher--open': open }"
                    v-bind="attrs"
                    v-on="on"
                    @click="toggle"
                >
                    <v-icon size="28">{{ open ? 'mdi-close' : 'mdi-robot-happy-outline' }}</v-icon>
                </v-btn>
            </template>
            <span>{{ open ? 'Close the assistant' : 'GGB AI assistant' }}</span>
        </v-tooltip>

        <!-- Panel -->
        <transition name="ai-chat-pop">
            <div
                v-show="open"
                class="ai-chat__panel elevation-12"
                :class="{ 'ai-chat__panel--resizing': resizing }"
                :style="panelStyle"
                @dragenter.prevent="onDragEnter"
                @dragover.prevent="onDragOver"
                @dragleave="onDragLeave"
                @drop.prevent="onDrop"
                @paste="onPaste"
            >
                <!-- Shown while files are dragged over the panel -->
                <div v-if="dragDepth > 0" class="ai-chat__dropzone" :class="{ 'ai-chat__dropzone--off': !canAttach }">
                    <v-icon large :color="canAttach ? '#2F6FA3' : 'grey'">{{ canAttach ? 'mdi-tray-arrow-down' : 'mdi-cancel' }}</v-icon>
                    <div class="ai-chat__dropzone-title">{{ canAttach ? 'Drop files to attach' : dropBlockedReason }}</div>
                    <div v-if="canAttach" class="ai-chat__dropzone-text">
                        PDF, image, Excel, CSV or text · up to {{ limits.max_attachment_mb }} MB each · {{ limits.max_attachments - files.length }} more file{{ limits.max_attachments - files.length === 1 ? '' : 's' }}
                    </div>
                </div>
                <!-- Drag the top/left edges or the top-left corner to resize; double-click the corner to reset -->
                <div class="ai-chat__resize ai-chat__resize--top" @pointerdown.prevent="startResize('top', $event)"></div>
                <div class="ai-chat__resize ai-chat__resize--left" @pointerdown.prevent="startResize('left', $event)"></div>
                <!-- Visible grip in the top-left corner: pull up/left to make the window bigger, double-click to reset -->
                <div class="ai-chat__grip" title="Drag to resize · double-click to reset" @pointerdown.prevent="startResize('corner', $event)" @dblclick="resetSize">
                    <v-icon x-small color="white">mdi-arrow-top-left-bottom-right</v-icon>
                </div>
                <div class="ai-chat__header">
                    <v-btn v-if="view !== 'chat'" icon small dark class="mr-1" @click="view === 'transcript' ? openHistory() : (view = 'chat')">
                        <v-icon small>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-icon v-else small color="white" class="mr-2">mdi-robot-happy-outline</v-icon>
                    <div v-if="view === 'history'" class="ai-chat__title">
                        <div class="ai-chat__name">Past sessions</div>
                        <div class="ai-chat__page">Read back earlier conversations</div>
                    </div>
                    <div v-else-if="view === 'transcript' && transcript" class="ai-chat__title">
                        <div class="ai-chat__name" :title="transcript.title">{{ transcript.title }}</div>
                        <div class="ai-chat__page">{{ formatWhen(transcript.started_at) }}{{ transcript.page_label ? ` · ${transcript.page_label}` : '' }}</div>
                    </div>
                    <div v-else class="ai-chat__title">
                        <div class="ai-chat__name">GGB Assistant</div>
                        <div class="ai-chat__page" :title="pageTitle">
                            <v-icon x-small color="white" class="mr-1">{{ pageIcon }}</v-icon>{{ pageTitle }}
                        </div>
                    </div>
                    <v-tooltip v-if="view === 'chat'" bottom max-width="260">
                        <template v-slot:activator="{ on, attrs }">
                            <span class="ai-chat__model" :class="{ 'ai-chat__model--advanced': isAdvanced }" v-bind="attrs" v-on="on">
                                {{ isAdvanced ? 'Advanced' : 'Standard' }}
                            </span>
                        </template>
                        <span v-if="isAdvanced">Advanced model{{ session && session.model_reason ? ` (${session.model_reason})` : '' }}. Stays on for the rest of this session.</span>
                        <span v-else>Standard model. Switches to the advanced model for documents and complex changes.</span>
                        <span v-if="session && session.cost_usd"> This session: ${{ session.cost_usd.toFixed(3) }}.</span>
                    </v-tooltip>
                    <div class="ai-chat__actions" :class="{ 'ai-chat__actions--compact': panelWidth < 460 }">
                        <button type="button" class="ai-chat__action" :class="{ 'ai-chat__action--active': view !== 'chat' }" :disabled="busy" title="Read back past sessions" @click="openHistory">
                            <v-icon x-small color="white">mdi-history</v-icon><span class="ai-chat__action-label">History</span>
                        </button>
                        <button type="button" class="ai-chat__action" :disabled="busy || !hasLiveMessages" title="Start a new session" @click="startNewSession(false)">
                            <v-icon x-small color="white">mdi-plus</v-icon><span class="ai-chat__action-label">New</span>
                        </button>
                        <button type="button" class="ai-chat__action" :disabled="busy || !hasLiveMessages" title="Start a new session that carries a summary of this one" @click="startNewSession(true)">
                            <v-icon x-small color="white">mdi-transfer-right</v-icon><span class="ai-chat__action-label">Handover</span>
                        </button>
                    </div>
                    <v-btn icon small dark title="Minimise" @click="toggle">
                        <v-icon small>mdi-chevron-down</v-icon>
                    </v-btn>
                </div>

                <!-- Session warning / page notice -->
                <div v-if="view === 'chat' && sessionWarning && !sessionEnded" class="ai-chat__banner ai-chat__banner--warn">
                    <v-icon x-small color="orange darken-2" class="mr-1">mdi-timer-sand</v-icon>
                    <span>{{ sessionWarning }}</span>
                    <button type="button" class="ai-chat__link ml-auto" :disabled="busy" @click="startNewSession(true)">New with handover</button>
                </div>
                <div v-if="view === 'chat' && context && context.notice" class="ai-chat__banner">
                    <v-icon x-small color="blue-grey" class="mr-1">mdi-information-outline</v-icon>
                    <span>{{ context.notice }}</span>
                </div>

                <!-- Past sessions -->
                <div v-if="view === 'history'" class="ai-chat__messages ai-chat__history">
                    <div v-if="historyLoading && !history.length" class="ai-chat__empty">
                        <v-progress-circular indeterminate size="24" color="#5B95C7" />
                    </div>
                    <div v-else-if="!history.length" class="ai-chat__empty">
                        <v-icon large color="#93B9DC">mdi-history</v-icon>
                        <div class="ai-chat__empty-title">No past sessions yet</div>
                        <div class="ai-chat__empty-text">Conversations are kept here so you can read them back after they end.</div>
                    </div>
                    <div
                        v-for="item in history"
                        :key="item.id"
                        role="button"
                        tabindex="0"
                        class="ai-chat__history-item"
                        :class="{ 'ai-chat__history-item--current': session && item.id === session.id }"
                        @click="openTranscript(item)"
                        @keydown.enter="openTranscript(item)"
                    >
                        <div class="d-flex align-center">
                            <div class="ai-chat__history-title">{{ item.title }}</div>
                            <v-btn icon x-small class="ai-chat__history-delete" title="Rename this session" @click.stop="askRename(item)">
                                <v-icon x-small>mdi-pencil-outline</v-icon>
                            </v-btn>
                            <v-btn icon x-small class="ai-chat__history-delete ml-0" title="Delete this session" @click.stop="askDelete(item)">
                                <v-icon x-small>mdi-delete-outline</v-icon>
                            </v-btn>
                        </div>
                        <div class="ai-chat__history-meta">
                            {{ formatWhen(item.last_message_at) }} · {{ item.turns }} message{{ item.turns === 1 ? '' : 's' }}
                            <span v-if="item.page_label"> · {{ item.page_label }}</span>
                            <span v-if="item.model === 'advanced'" class="ai-chat__history-tag">Advanced</span>
                            <span v-if="session && item.id === session.id" class="ai-chat__history-tag ai-chat__history-tag--current">Current</span>
                        </div>
                    </div>
                    <div v-if="historyHasMore" class="text-center py-2">
                        <v-btn small text color="#2F6FA3" :loading="historyLoading" @click="loadHistory(historyPage + 1)">Show older</v-btn>
                    </div>
                </div>

                <!-- Conversation (the live one, or a past session read-only) -->
                <div v-else ref="scroller" class="ai-chat__messages">
                    <div v-if="view === 'transcript' && transcriptLoading" class="ai-chat__empty">
                        <v-progress-circular indeterminate size="24" color="#5B95C7" />
                    </div>
                    <div v-if="view === 'chat' && !hasMessages && !busy" class="ai-chat__empty">
                        <div class="ai-chat__empty-title">Ask me to look something up or make a change</div>
                        <div class="ai-chat__empty-text">
                            I work on the page you're on{{ context && context.kind !== 'page' ? ` (${context.label})` : '' }}.
                            Bigger changes are previewed first and only applied after you confirm.
                        </div>
                        <div class="ai-chat__can-do">
                            <div class="ai-chat__can-do-title">
                                <v-icon small color="#2F6FA3" class="mr-1">mdi-star-four-points-outline</v-icon>What you can do with this assistant
                            </div>
                            <ul class="ai-chat__can-do-list">
                                <li v-for="item in canDoList" :key="item">{{ item }}</li>
                            </ul>
                        </div>
                        <div class="ai-chat__mindful">
                            <v-icon x-small color="orange darken-2" class="mr-1">mdi-account-check-outline</v-icon>
                            <span>
                                <strong>A tool, not a shortcut.</strong> The assistant is only as good as the person using it,
                                and it can still make mistakes. Ask with care, check what it gives you, and use your own
                                expertise before anything goes to a client, supplier or the books. Your attention to detail is what makes it work.
                            </span>
                        </div>
                    </div>

                    <div v-for="(m, index) in messages" :key="index" class="ai-chat__row" :class="`ai-chat__row--${m.role}`">
                        <div v-if="m.role === 'note'" class="ai-chat__note">
                            <div class="ai-chat__note-title"><v-icon x-small class="mr-1">mdi-transfer-right</v-icon>Continued from your previous session</div>
                            <div class="ai-chat__md" v-html="render(m.text)"></div>
                        </div>
                        <div v-else class="ai-chat__msg">
                            <div v-if="m.role === 'user'">
                                <div v-if="m.attachments && m.attachments.length" class="ai-chat__msg-files">
                                    <span v-for="name in m.attachments" :key="name" class="ai-chat__file-pill">
                                        <v-icon x-small class="mr-1">mdi-paperclip</v-icon>{{ name }}
                                    </span>
                                </div>
                                <div class="ai-chat__user-text">{{ m.text }}</div>
                            </div>
                            <template v-else>
                                <div class="ai-chat__md" v-html="render(m.text)"></div>
                                <div v-if="m.tools && m.tools.length" class="ai-chat__msg-tools">
                                    <v-icon x-small class="mr-1">mdi-wrench-outline</v-icon>{{ m.tools.join(' · ') }}
                                </div>
                                <div v-if="view === 'chat' && m.changed && index === lastChangedIndex" class="ai-chat__changed">
                                    <v-icon x-small color="success" class="mr-1">mdi-check-circle</v-icon>
                                    {{ m.awaiting_confirmation ? 'Some changes were saved and the page\'s data was refreshed.' : 'Changes were saved and the page\'s data was refreshed.' }}
                                    <button type="button" class="ai-chat__link ml-1" @click="refreshPage">Reload the whole page</button>
                                </div>
                                <div v-if="view === 'chat' && m.awaiting_confirmation && index === messages.length - 1" class="ai-chat__awaiting">
                                    <v-icon x-small color="orange darken-2" class="mr-1">mdi-clock-alert-outline</v-icon>
                                    {{ m.changed ? 'The rest is a preview: nothing more is applied until you confirm.' : 'This is a preview: nothing is applied until you confirm.' }}
                                </div>
                            </template>
                        </div>
                    </div>

                    <!-- Live progress -->
                    <div v-if="busy && view === 'chat'" class="ai-chat__row ai-chat__row--assistant">
                        <div class="ai-chat__msg ai-chat__progress">
                            <div v-for="(step, i) in progress" :key="i" class="ai-chat__step" :class="`ai-chat__step--${step.status}`">
                                <v-icon x-small class="mr-1" :color="stepColor(step)">{{ stepIcon(step) }}</v-icon>{{ step.label }}
                            </div>
                            <div class="ai-chat__typing"><span></span><span></span><span></span></div>
                        </div>
                    </div>
                </div>

                <!-- Reading a past session -->
                <div v-if="view === 'transcript'" class="ai-chat__readonly">
                    <span>
                        <v-icon x-small class="mr-1">mdi-eye-outline</v-icon>
                        {{ transcript && session && transcript.id === session.id ? 'Your current session' : 'Read only: this session has ended' }}
                    </span>
                    <button v-if="transcript && transcript.messages" type="button" class="ai-chat__link ml-auto mr-3" @click="askRename(transcript)">
                        <v-icon x-small color="#2F6FA3">mdi-pencil-outline</v-icon> Rename
                    </button>
                    <button v-if="transcript && transcript.messages" type="button" class="ai-chat__link ai-chat__link--danger mr-3" @click="askDelete(transcript)">
                        <v-icon x-small color="error">mdi-delete-outline</v-icon> Delete
                    </button>
                    <button type="button" class="ai-chat__link" :class="{ 'ml-auto': !(transcript && transcript.messages) }" @click="view = 'chat'">Back to chat</button>
                </div>

                <template v-else-if="view === 'history'"></template>

                <template v-else>
                    <!-- Session limit reached: the box stays visible but locked, with what to do next -->
                    <div v-if="sessionLocked" class="ai-chat__ended">
                        <div class="ai-chat__ended-text">
                            <v-icon small color="orange darken-2" class="mr-1">mdi-timer-off-outline</v-icon>
                            <strong>{{ sessionEndedReason }}</strong>
                            Start a new session to carry on. <strong>Handover</strong> brings a summary of this one with it.
                        </div>
                        <div class="d-flex">
                            <v-btn small depressed color="#2F6FA3" dark class="mr-2" :loading="busy" :disabled="!hasLiveMessages" @click="startNewSession(true)">
                                <v-icon left x-small>mdi-transfer-right</v-icon>Handover
                            </v-btn>
                            <v-btn small outlined color="#2F6FA3" :disabled="busy" @click="startNewSession(false)">
                                <v-icon left x-small>mdi-plus</v-icon>New session
                            </v-btn>
                        </div>
                    </div>

                    <!-- Suggestions: gaps first (amber), then everyday changes. Clicking fills the box; it never sends. -->
                    <div v-if="!busy && !sessionLocked && (gapChips.length || otherChips.length)" class="ai-chat__chips">
                        <div v-if="gapChips.length" class="ai-chat__chip-row">
                            <span class="ai-chat__chips-label ai-chat__chips-label--gap">
                                <v-icon x-small color="amber darken-3">mdi-alert-decagram</v-icon> Fix
                            </span>
                            <v-chip
                                v-for="chip in gapChips"
                                :key="chip.key"
                                small
                                color="amber lighten-4"
                                text-color="brown darken-3"
                                class="ai-chat__chip ai-chat__chip--gap"
                                :title="chip.text"
                                @click="useChip(chip)"
                            >
                                <v-icon x-small left>{{ chip.icon }}</v-icon>{{ chip.label }}
                            </v-chip>
                        </div>
                        <div v-if="otherChips.length" class="ai-chat__chip-row">
                            <span class="ai-chat__chips-label">Try</span>
                            <v-chip
                                v-for="chip in otherChips"
                                :key="chip.key"
                                small
                                outlined
                                color="#2F6FA3"
                                class="ai-chat__chip"
                                :title="chip.text"
                                @click="useChip(chip)"
                            >
                                <v-icon x-small left>{{ chip.icon }}</v-icon>{{ chip.label }}
                            </v-chip>
                        </div>
                    </div>

                    <div v-if="error" class="ai-chat__error">
                        <v-icon x-small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>{{ error }}
                        <button type="button" class="ai-chat__link ml-auto" @click="error = ''">Dismiss</button>
                    </div>

                    <!-- Composer -->
                    <div class="ai-chat__composer">
                        <div v-if="files.length" class="ai-chat__files">
                            <span v-for="(file, i) in files" :key="file.name + i" class="ai-chat__file-pill">
                                <v-icon x-small class="mr-1">mdi-paperclip</v-icon>{{ file.name }}
                                <v-icon x-small class="ml-1 ai-chat__file-remove" @click="files.splice(i, 1)">mdi-close</v-icon>
                            </span>
                        </div>
                        <div class="d-flex align-end">
                            <v-btn icon small class="mb-1" :disabled="busy || sessionLocked || files.length >= limits.max_attachments" title="Attach a rate sheet, contract, email or a screenshot (PDF, image, Excel, CSV, text). You can also drag files here or paste an image with Ctrl+V" @click="$refs.fileInput.click()">
                                <v-icon small>mdi-paperclip</v-icon>
                            </v-btn>
                            <input ref="fileInput" type="file" multiple class="d-none" :accept="acceptList" @change="onFiles" />
                            <v-textarea
                                ref="input"
                                v-model="text"
                                :placeholder="placeholder"
                                rows="1"
                                row-height="20"
                                auto-grow
                                solo
                                flat
                                dense
                                hide-details
                                background-color="transparent"
                                class="ai-chat__textarea"
                                :disabled="busy || sessionLocked"
                                @keydown.enter.exact.prevent="send"
                            />
                            <v-btn
                                fab
                                x-small
                                depressed
                                color="#2F6FA3"
                                class="mb-1"
                                :dark="canSend"
                                :loading="busy"
                                :disabled="!canSend"
                                @click="send"
                            >
                                <v-icon small>mdi-send</v-icon>
                            </v-btn>
                        </div>
                    </div>
                </template>
            </div>
        </transition>

        <v-dialog v-model="renameDialog" max-width="420">
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">Rename session</v-card-title>
                <v-card-text>
                    <v-text-field
                        v-model="renameText"
                        label="Name"
                        counter="200"
                        maxlength="200"
                        autofocus
                        outlined
                        dense
                        @keydown.enter="confirmRename"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text :disabled="renaming" @click="renameDialog = false">Cancel</v-btn>
                    <v-btn depressed color="#2F6FA3" dark :loading="renaming" :disabled="!renameText.trim()" @click="confirmRename">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="deleteDialog" max-width="400">
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">Delete this session?</v-card-title>
                <v-card-text>
                    <div class="mb-2">"{{ deleteTarget && deleteTarget.title }}" will be removed from your history. This can't be undone.</div>
                    <div v-if="deleteTarget && session && deleteTarget.id === session.id" class="orange--text text--darken-3">
                        This is your current session, so it will also end and the chat will start fresh.
                    </div>
                    <div class="grey--text text-caption mt-2">Changes the assistant already made in GoGroupBooking stay as they are.</div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text :disabled="deleting" @click="deleteDialog = false">Cancel</v-btn>
                    <v-btn depressed color="error" :loading="deleting" @click="confirmDelete">Delete</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { renderMarkdown } from './markdown'

// Floating Claude assistant (bottom right, under the back-to-top button). It talks to ggb_api
// api/ai_chat, which runs Claude with the MCP tools as the signed-in user; see
// docs/reference/AI_CHAT_BUBBLE.md. The conversation lives on the server, so it survives reloads.
const OPEN_KEY = 'ggb-ai-chat-open'
// Same list as AiChatAttachment::supported() in ggb_api
const ACCEPTED_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'xlsx', 'xls', 'csv', 'txt', 'eml', 'md']
const DEFAULT_WIDTH = 520
const DEFAULT_HEIGHT = 660
const MIN_WIDTH = 340
const MIN_HEIGHT = 380
// Room the panel must leave: the launcher below it (bottom 108px) and the app bar above it
const RESERVED_HEIGHT = 200
const SIDE_MARGIN = 40

function readToken() {
    try {
        return JSON.parse(localStorage.getItem('vuex'))?.auth?.token || null
    } catch (e) {
        return null
    }
}

function apiUrl(path) {
    return String(process.env.VUE_APP_API_URL || '').replace(/\/+$/, '') + '/' + path.replace(/^\/+/, '')
}

function readFile(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = () => reject(reader.error)
        reader.readAsDataURL(file)
    })
}

export default {
    name: 'AiChatBubble',
    data() {
        return {
            available: false,
            open: false,
            session: null,
            context: null,
            limits: { max_attachment_mb: 5, max_attachments: 20, max_total_mb: 20 },
            text: '',
            files: [],
            // The text came from a chip marked complex (runs on the advanced model)
            chipComplex: false,
            busy: false,
            progress: [],
            pendingMessages: [],
            error: '',
            contextTimer: null,
            // 'chat' | 'history' (list of past sessions) | 'transcript' (one past session, read only)
            view: 'chat',
            history: [],
            historyPage: 1,
            historyHasMore: false,
            historyLoading: false,
            transcript: null,
            transcriptLoading: false,
            // Size while dragging; saved to vuex (ai_chat module) when the drag ends
            resizing: false,
            dragSize: null,
            resizeStart: null,
            viewport: { width: window.innerWidth, height: window.innerHeight },
            dragDepth: 0,
            // Ticks while the bubble is mounted, so a session that runs out of time locks itself
            now: Date.now(),
            clockTimer: null,
            deleteDialog: false,
            deleteTarget: null,
            deleting: false,
            renameDialog: false,
            renameTarget: null,
            renameText: '',
            renaming: false,
        }
    },
    computed: {
        acceptList() {
            return ACCEPTED_EXTENSIONS.map(e => `.${e}`).join(',')
        },
        canAttach() {
            return this.view === 'chat' && !this.busy && !this.sessionEnded && this.files.length < this.limits.max_attachments
        },
        dropBlockedReason() {
            if (this.view !== 'chat') return 'Go back to the chat to attach files'
            if (this.sessionEnded) return 'Start a new session to attach files'
            if (this.busy) return 'Wait for the current reply'
            return `Up to ${this.limits.max_attachments} files per message`
        },
        messages() {
            if (this.view === 'transcript') return this.transcript?.messages || []
            return [...(this.session?.messages || []), ...this.pendingMessages]
        },
        hasMessages() {
            return this.messages.length > 0
        },
        hasLiveMessages() {
            return (this.session?.messages || []).length > 0
        },
        isPhone() {
            return this.viewport.width <= 600
        },
        panelWidth() {
            if (this.isPhone) return this.viewport.width - 32
            const width = (this.dragSize || this.$store.getters['ai_chat/get_panel_size']).width || DEFAULT_WIDTH
            return this.clampSize(width, DEFAULT_HEIGHT).width
        },
        // Saved size (or the drag in progress), kept inside the window
        panelStyle() {
            if (this.isPhone) return {}
            const saved = this.dragSize || this.$store.getters['ai_chat/get_panel_size']
            if (!saved.width && !saved.height) return {}
            const size = this.clampSize(saved.width || DEFAULT_WIDTH, saved.height || DEFAULT_HEIGHT)
            return { width: `${size.width}px`, height: `${size.height}px` }
        },
        isAdvanced() {
            return this.session?.model === 'advanced'
        },
        // Why the session can't take more messages: what the server said, or a time limit the
        // clock has passed since (the server checks the same limits on the next send)
        clientEndedReason() {
            const state = this.session?.state
            if (!state) return null
            if (state.ended) return state.ended_reason || 'This session has ended.'
            if (state.ends_at && this.now >= Date.parse(state.ends_at)) return 'This session reached its time limit.'
            if (state.idle_until && this.now >= Date.parse(state.idle_until)) return 'This session timed out after a while without activity.'
            return null
        },
        sessionEnded() {
            return this.clientEndedReason !== null
        },
        sessionLocked() {
            return this.sessionEnded
        },
        sessionEndedReason() {
            return this.clientEndedReason || 'This session has ended.'
        },
        // Recomputed from the clock (ticks every 10 s), so the countdown moves without a refresh.
        // Time warnings come from ends_at / idle_until; the server's own warning covers the rest
        // (messages left, conversation length).
        sessionWarning() {
            const state = this.session?.state
            if (!state || this.sessionEnded) return ''
            const minutes = iso => Math.max(1, Math.ceil((Date.parse(iso) - this.now) / 60000))
            const idleLeft = state.idle_until ? Date.parse(state.idle_until) - this.now : Infinity
            if (idleLeft <= 5 * 60000) {
                return `This session times out in about ${minutes(state.idle_until)} minute${minutes(state.idle_until) === 1 ? '' : 's'} without activity.`
            }
            if (state.ends_at && state.time_warning_from && this.now >= Date.parse(state.time_warning_from)) {
                const left = minutes(state.ends_at)
                return `This session ends in about ${left} minute${left === 1 ? '' : 's'}.`
            }
            const server = state.warning || ''
            return /ends in about/i.test(server) ? '' : server
        },
        lastChangedIndex() {
            for (let i = this.messages.length - 1; i >= 0; i--) {
                if (this.messages[i].changed) return i
            }
            return -1
        },
        canSend() {
            return !this.busy && (this.text.trim() !== '' || this.files.length > 0)
        },
        // Plain-language list of what this user's tools allow (AiChatAccess caps from the server)
        canDoList() {
            const caps = this.$store.getters['ai_chat/get_caps']
            const list = [
                'Answer questions about leads, quotes, events, payments and supplier bookings, with reports and period comparisons',
                'Export any of that data to a CSV (opens in Google Sheets or Excel), then ask for different columns or extra data joined in',
                'Read and summarise an event: the booked quote, group size, deposit, balance and supplier bookings',
            ]
            if (caps.edit) list.push('Change quotes and event bookings you have access to; every high-risk change is shown to you as a preview to confirm first')
            if (caps.create) list.push('Create new quotes and Copy Quote variants for a lead')
            if (caps.suppliers) list.push('Find, create and update suppliers, products, options and rates, including from a rate sheet or contract you attach')
            if (caps.history) list.push('Read the history of an event and its bookings, and the emails sent')
            if (caps.client) list.push('Look a client up in GHL while you are on a call with them: notes, latest messages, calls and their enquiries')
            if (caps.calls) list.push('Report on phone calls and read their transcripts')
            if (caps.legacy) list.push('Report on bookings made before the move to GoGroupBooking, in MyStagit and the old FreedomLtd system')
            if (caps.terms) list.push('Answer questions from our terms and conditions, such as cancellation or deposit terms')
            return list
        },
        gapChips() {
            return (this.context?.suggestions || []).filter(c => c.priority === 'gap')
        },
        otherChips() {
            return (this.context?.suggestions || []).filter(c => c.priority !== 'gap')
        },
        pageTitle() {
            if (!this.context) return 'Loading page…'
            return this.context.subtitle ? `${this.context.label} · ${this.context.subtitle}` : this.context.label
        },
        pageIcon() {
            return { lead: 'mdi-account-search-outline', event: 'mdi-calendar-star', supplier: 'mdi-domain' }[this.context?.kind] || 'mdi-file-outline'
        },
        placeholder() {
            if (this.context?.kind === 'lead') return 'Ask about this lead…'
            if (this.context?.kind === 'event') return 'Ask about this event…'
            if (this.context?.kind === 'supplier') return 'Ask about this supplier…'
            return 'Ask the assistant…'
        },
    },
    watch: {
        // Another screen asked the bubble to send something (e.g. "Ask the assistant" buttons on other pages)
        '$store.state.ai_chat.pending_request'(request) {
            if (request) this.handleRequest(request)
        },
        '$route.fullPath'() {
            if (this.open) this.loadContextSoon()
        },
        '$store.state.auth.selected_company.id'() {
            if (this.open) this.loadContextSoon()
        },
        text(value) {
            // A chip's "complex" flag lasts while its text is still recognisably there
            if (this.chipComplex && !value.trim()) this.chipComplex = false
        },
    },
    async mounted() {
        const res = await this.$axios.get('api/ai_chat/status')
        const available = !!(res?.data?.response && res.data.available)
        this.$store.commit('ai_chat/set_available', available)
        this.$store.commit('ai_chat/set_caps', available ? res.data.caps : {})
        // A request left over from before a reload is stale
        this.$store.commit('ai_chat/clear_request')
        if (!available) return
        this.available = true
        this.session = res.data.session
        this.limits = res.data.limits || this.limits
        this.$emit('available', true)
        let reopen = false
        try {
            reopen = sessionStorage.getItem(OPEN_KEY) === '1'
        } catch (e) {
            reopen = false
        }
        // Only reached for users the API says have the bubble. First time they see it, it opens by
        // itself; once they close it, it stays closed on load
        const userId = this.$store.getters['auth/get_user']?.id
        if (reopen || !this.$store.getters['ai_chat/is_intro_seen'](userId)) this.toggle()
    },
    created() {
        window.addEventListener('resize', this.onWindowResize)
        this.clockTimer = setInterval(() => {
            this.now = Date.now()
        }, 10000)
    },
    beforeDestroy() {
        clearInterval(this.clockTimer)
        clearTimeout(this.contextTimer)
        window.removeEventListener('resize', this.onWindowResize)
        this.stopResize()
    },
    methods: {
        // ---------- Resizing (the panel is anchored bottom-right, so it grows up and to the left) ----------
        onWindowResize() {
            this.viewport = { width: window.innerWidth, height: window.innerHeight }
        },
        clampSize(width, height) {
            const maxWidth = Math.max(MIN_WIDTH, this.viewport.width - SIDE_MARGIN)
            const maxHeight = Math.max(MIN_HEIGHT, this.viewport.height - RESERVED_HEIGHT)
            return {
                width: Math.round(Math.min(maxWidth, Math.max(MIN_WIDTH, width))),
                height: Math.round(Math.min(maxHeight, Math.max(MIN_HEIGHT, height))),
            }
        },
        startResize(edge, event) {
            if (this.isPhone) return
            const rect = this.$el.querySelector('.ai-chat__panel').getBoundingClientRect()
            this.resizeStart = { edge, x: event.clientX, y: event.clientY, width: rect.width, height: rect.height }
            this.dragSize = { width: rect.width, height: rect.height }
            this.resizing = true
            window.addEventListener('pointermove', this.onResize)
            window.addEventListener('pointerup', this.endResize)
        },
        onResize(event) {
            const s = this.resizeStart
            if (!s) return
            this.dragSize = this.clampSize(
                s.edge === 'top' ? s.width : s.width + (s.x - event.clientX),
                s.edge === 'left' ? s.height : s.height + (s.y - event.clientY),
            )
        },
        endResize() {
            if (this.dragSize) this.$store.commit('ai_chat/set_panel_size', this.dragSize)
            this.stopResize()
        },
        stopResize() {
            window.removeEventListener('pointermove', this.onResize)
            window.removeEventListener('pointerup', this.endResize)
            this.resizing = false
            this.dragSize = null
            this.resizeStart = null
        },
        resetSize() {
            this.$store.commit('ai_chat/set_panel_size', { width: null, height: null })
        },
        // ---------- Past sessions ----------
        async openHistory() {
            this.view = 'history'
            this.transcript = null
            await this.loadHistory(1)
        },
        async loadHistory(page) {
            this.historyLoading = true
            const res = await this.$axios.get('api/ai_chat/history', { page })
            this.historyLoading = false
            if (!res?.data?.response) {
                this.error = res?.data?.message || 'Past sessions could not be loaded.'
                return
            }
            this.history = page === 1 ? res.data.sessions : [...this.history, ...res.data.sessions]
            this.historyPage = page
            this.historyHasMore = !!res.data.has_more
        },
        async openTranscript(item) {
            this.view = 'transcript'
            this.transcript = { ...item, messages: [] }
            this.transcriptLoading = true
            const res = await this.$axios.get(`api/ai_chat/history/${item.id}`)
            this.transcriptLoading = false
            if (res?.data?.response) {
                this.transcript = res.data.session
                this.$nextTick(() => {
                    const el = this.$refs.scroller
                    if (el) el.scrollTop = 0
                })
            } else {
                this.error = res?.data?.message || 'That session could not be loaded.'
                this.view = 'history'
            }
        },
        askRename(item) {
            this.renameTarget = item
            this.renameText = item.title || ''
            this.renameDialog = true
        },
        async confirmRename() {
            const target = this.renameTarget
            const title = this.renameText.trim()
            if (!target || !title || this.renaming) return
            this.renaming = true
            const res = await this.$axios.post(`api/ai_chat/history/${target.id}/rename`, { title })
            this.renaming = false
            if (!res?.data?.response) {
                this.$toast.error(res?.data?.message || 'The session could not be renamed.')
                return
            }
            const saved = res.data.title
            this.history = this.history.map(h => (h.id === target.id ? { ...h, title: saved } : h))
            if (this.transcript && this.transcript.id === target.id) this.transcript = { ...this.transcript, title: saved }
            if (this.session && this.session.id === target.id) this.session = { ...this.session, title: saved }
            this.renameDialog = false
        },
        askDelete(item) {
            this.deleteTarget = item
            this.deleteDialog = true
        },
        async confirmDelete() {
            const target = this.deleteTarget
            if (!target) return
            this.deleting = true
            let res = null
            try {
                res = await this.$axios.delete(`api/ai_chat/history/${target.id}`)
            } catch (err) {
                res = err
            }
            this.deleting = false
            if (!res?.data?.response) {
                this.error = res?.data?.message || 'That session could not be deleted.'
                this.deleteDialog = false
                return
            }
            this.history = this.history.filter(h => h.id !== target.id)
            if (res.data.live_session_ended) {
                // The current session went with it: start the chat empty
                this.session = null
                this.pendingMessages = []
            }
            this.deleteDialog = false
            this.deleteTarget = null
            if (this.view === 'transcript') this.view = 'history'
            this.$toast.success('Session deleted')
        },
        formatWhen(iso) {
            if (!iso) return ''
            const d = this.$date(iso)
            return d.isValid() ? d.format('ddd D MMM YYYY, HH:mm') : ''
        },
        render(text) {
            return renderMarkdown(text)
        },
        toggle() {
            this.open = !this.open
            try {
                sessionStorage.setItem(OPEN_KEY, this.open ? '1' : '0')
            } catch (e) {
                // Storage blocked: the bubble just starts closed next time
            }
            if (!this.open) this.$store.commit('ai_chat/set_intro_seen', this.$store.getters['auth/get_user']?.id)
            if (this.open) {
                this.loadContext()
                this.$nextTick(() => {
                    this.scrollDown()
                    this.$refs.input?.focus()
                })
            }
        },
        loadContextSoon() {
            clearTimeout(this.contextTimer)
            this.contextTimer = setTimeout(this.loadContext, 400)
        },
        currentPage() {
            // The company switcher's choice, so answers and new records use it (the server checks access)
            const company = this.$store.getters['auth/get_selected_company']
            return {
                name: this.$route.name || '',
                params: { ...this.$route.params },
                path: this.$route.path,
                company: company && company.id ? { id: company.id } : null,
            }
        },
        async loadContext() {
            const res = await this.$axios.post('api/ai_chat/context', { page: this.currentPage() })
            if (res?.data?.response) this.context = res.data.context
        },
        useChip(chip) {
            this.text = chip.text
            this.chipComplex = !!chip.complex
            if (chip.attach) this.$refs.fileInput?.click()
            this.$nextTick(() => {
                const el = this.$refs.input?.$el?.querySelector('textarea')
                if (!el) return
                el.focus()
                // Select the first [placeholder] so it can be typed over
                const start = chip.text.indexOf('[')
                const end = chip.text.indexOf(']', start)
                if (start >= 0 && end > start) el.setSelectionRange(start, end + 1)
            })
        },
        onFiles(event) {
            const picked = Array.from(event.target.files || [])
            event.target.value = ''
            this.addFiles(picked)
        },
        // Picker and drag-and-drop both land here: checks type, size, the per-message count and total size, and
        // says which files were skipped and why.
        async addFiles(list) {
            const problems = []
            const maxTotal = (this.limits.max_total_mb || 20) * 1024 * 1024
            let total = this.files.reduce((sum, f) => sum + (f.size || 0), 0)
            for (const file of list) {
                const extension = (file.name.split('.').pop() || '').toLowerCase()
                if (!file.name.includes('.') || !ACCEPTED_EXTENSIONS.includes(extension)) {
                    problems.push(`${file.name} is not a supported type`)
                    continue
                }
                if (file.size > this.limits.max_attachment_mb * 1024 * 1024) {
                    problems.push(`${file.name} is larger than ${this.limits.max_attachment_mb} MB`)
                    continue
                }
                if (this.files.some(f => f.name === file.name)) {
                    continue
                }
                if (this.files.length >= this.limits.max_attachments) {
                    problems.push(`only ${this.limits.max_attachments} files can go with one message`)
                    break
                }
                if (total + file.size > maxTotal) {
                    problems.push(`${file.name} would take this message over ${this.limits.max_total_mb || 20} MB in all (send it in the next message)`)
                    continue
                }
                try {
                    this.files.push({ name: file.name, size: file.size, data: await readFile(file) })
                    total += file.size
                } catch (e) {
                    problems.push(`${file.name} could not be read`)
                }
            }
            this.error = problems.length ? `Not attached: ${problems.join('; ')}.` : ''
            if (this.files.length) this.$nextTick(() => this.$refs.input?.focus())
        },
        // ---------- Paste (Ctrl+V) ----------
        // Text pastes into the box as usual; pasted files and screenshots are attached instead.
        onPaste(event) {
            const items = Array.from(event.clipboardData?.items || [])
            const files = items.filter(i => i.kind === 'file').map(i => i.getAsFile()).filter(Boolean)
            if (!files.length) return
            event.preventDefault()
            if (!this.canAttach) {
                this.error = this.dropBlockedReason
                return
            }
            const stamp = this.$date().format('HHmmss')
            this.addFiles(files.map((file, n) => {
                // Screenshots arrive as "image.png" every time: give each a name of its own
                if (!/^image\.(png|jpe?g|gif|webp)$/i.test(file.name)) return file
                const extension = file.name.split('.').pop().toLowerCase()
                return new File([file], `pasted-image-${stamp}${files.length > 1 ? '-' + (n + 1) : ''}-${this.files.length + n + 1}.${extension}`, { type: file.type })
            }))
        },
        // ---------- Drag and drop ----------
        // dragenter/dragleave fire for every child element, so count depth to know when the drag
        // really left the panel. Only file drags count (not text or links dragged in).
        isFileDrag(event) {
            return Array.from(event.dataTransfer?.types || []).includes('Files')
        },
        onDragEnter(event) {
            if (this.isFileDrag(event)) this.dragDepth++
        },
        onDragOver(event) {
            if (this.isFileDrag(event)) event.dataTransfer.dropEffect = this.canAttach ? 'copy' : 'none'
        },
        onDragLeave(event) {
            if (this.isFileDrag(event) && this.dragDepth > 0) this.dragDepth--
        },
        onDrop(event) {
            const dropped = Array.from(event.dataTransfer?.files || [])
            this.dragDepth = 0
            if (!dropped.length || !this.canAttach) return
            this.addFiles(dropped)
        },
        stepIcon(step) {
            if (step.status === 'running') return 'mdi-progress-clock'
            if (step.status === 'error') return 'mdi-alert-circle-outline'
            if (step.status === 'model') return 'mdi-rocket-launch-outline'
            return 'mdi-check'
        },
        stepColor(step) {
            if (step.status === 'error') return 'orange'
            if (step.status === 'model') return '#2F6FA3'
            return step.status === 'running' ? 'grey' : 'success'
        },
        scrollDown() {
            this.$nextTick(() => {
                const el = this.$refs.scroller
                if (el) el.scrollTop = el.scrollHeight
            })
        },
        handleEvent(event) {
            if (event.type === 'tool') {
                const running = this.progress.find(s => s.name === event.name && s.status === 'running')
                // The finished step can be relabelled (a write tool that only previewed)
                if (running) Object.assign(running, { status: event.status, label: event.label })
                else this.progress.push({ name: event.name, label: event.label, status: event.status })
            } else if (event.type === 'model') {
                this.progress.push({ name: 'model', label: `Using the advanced model (${event.reason})`, status: 'model' })
                if (this.session) this.session = { ...this.session, model: 'advanced', model_reason: event.reason }
            } else if (event.type === 'done' || event.type === 'session_ended') {
                this.session = event.session
                this.pendingMessages = []
                if (event.message && event.message.changed) this.refreshPageData()
            } else if (event.type === 'quote_draft') {
                this.openQuoteDraft(event.draft)
            } else if (event.type === 'title') {
                // Generated after the first reply
                if (this.session) this.session = { ...this.session, title: event.title }
            } else if (event.type === 'error') {
                this.error = event.message
            }
            this.scrollDown()
        },
        async send() {
            if (!this.canSend || this.sessionEnded) return
            const body = {
                text: this.text.trim(),
                attachments: this.files,
                page: this.currentPage(),
                complex: this.chipComplex,
            }
            this.pendingMessages = [{ role: 'user', text: body.text, attachments: this.files.map(f => f.name) }]
            this.text = ''
            this.files = []
            this.chipComplex = false
            this.error = ''
            this.progress = []
            this.busy = true
            this.scrollDown()

            let gotDone = false
            try {
                const token = readToken()
                const response = await fetch(apiUrl('api/ai_chat/message'), {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/x-ndjson, application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify(body),
                })

                if (!response.ok || !(response.headers.get('Content-Type') || '').includes('ndjson')) {
                    const data = await response.json().catch(() => ({}))
                    const validation = data.errors ? Object.values(data.errors).flat()[0] : null
                    throw new Error(response.status === 401 ? 'Your sign-in has expired. Please sign in again.' : (validation || data.message || 'The assistant could not be reached.'))
                }

                // Progress events arrive one JSON object per line
                const reader = response.body.getReader()
                const decoder = new TextDecoder()
                let buffer = ''
                for (;;) {
                    const { value, done } = await reader.read()
                    if (done) break
                    buffer += decoder.decode(value, { stream: true })
                    let newline
                    while ((newline = buffer.indexOf('\n')) >= 0) {
                        const line = buffer.slice(0, newline).trim()
                        buffer = buffer.slice(newline + 1)
                        if (!line) continue
                        try {
                            const event = JSON.parse(line)
                            if (event.type === 'done' || event.type === 'session_ended') gotDone = true
                            this.handleEvent(event)
                        } catch (e) {
                            // A broken line: skip it, the final event carries the whole reply
                        }
                    }
                }
            } catch (e) {
                this.error = e.message || 'The assistant could not be reached.'
            } finally {
                this.busy = false
                this.progress = []
                if (!gotDone) {
                    // Put the unsent text back so it isn't lost
                    if (!this.text && this.pendingMessages[0]) this.text = this.pendingMessages[0].text
                    this.pendingMessages = []
                }
                this.scrollDown()
                this.$nextTick(() => this.$refs.input?.focus())
            }
        },
        async startNewSession(handover) {
            if (this.busy) return
            this.view = 'chat'
            this.busy = true
            this.progress = handover ? [{ name: 'handover', label: 'Writing a handover note', status: 'running' }] : []
            this.error = ''
            const res = await this.$axios.post('api/ai_chat/new_session', { handover })
            this.busy = false
            this.progress = []
            if (res?.data?.response) {
                this.session = res.data.session
                this.pendingMessages = []
                if (res.data.handover_failed) this.error = 'The handover note could not be written; this session starts fresh.'
                this.loadContext()
            } else {
                this.error = res?.data?.message || 'Could not start a new session.'
            }
            this.$nextTick(() => this.$refs.input?.focus())
        },
        // ---------- Requests from other screens ----------
        handleRequest(request) {
            this.$store.commit('ai_chat/clear_request')
            if (!this.available) return
            if (!this.open) this.toggle()
            this.view = 'chat'
            if (this.busy) {
                this.error = 'Wait for the current reply, then try again.'
                return
            }
            if (this.sessionLocked) {
                this.error = 'This session has ended: start a new session, then ask again.'
                return
            }
            this.text = request.text || ''
            this.chipComplex = !!request.complex
            this.$nextTick(() => this.send())
        },
        // After a reply that changed data: the page reloads its own data through its aiRefresh()
        // method (keeps tabs, filters and paging), else the whole page component is rebuilt so its
        // created()/mounted() run again. The chips are reloaded too, so fixed gaps disappear.
        async refreshPageData() {
            const matched = this.$route.matched
            let page = null
            for (let i = matched.length - 1; i >= 0 && !page; i--) {
                const vm = matched[i].instances && matched[i].instances.default
                if (vm && typeof vm.aiRefresh === 'function') page = vm
            }
            try {
                if (page) await page.aiRefresh()
                else this.$store.commit('ai_chat/reload_page')
            } catch (e) {
                this.$store.commit('ai_chat/reload_page')
            }
            if (this.open) this.loadContextSoon()
        },
        // A quote the assistant drafted (draft_quote): the quote editor loads it and opens on the
        // Review tab, where the user presses Create / Update. Already on that editor: load it now;
        // otherwise go there and the editor picks it up from the store when it mounts.
        openQuoteDraft(draft) {
            if (!draft || !draft.lead_id) return
            this.$store.commit('ai_chat/set_quote_draft', draft)
            const target = draft.quote_id
                ? { name: 'EditCustomQuote', params: { lead_id: String(draft.lead_id), quote_id: String(draft.quote_id) } }
                : { name: 'CreateCustomQuote', params: { lead_id: String(draft.lead_id) } }
            const params = this.$route.params || {}
            const here = this.$route.name === target.name && String(params.lead_id) === target.params.lead_id
                && (!draft.quote_id || String(params.quote_id) === target.params.quote_id)
            if (here) {
                const matched = this.$route.matched
                for (let i = matched.length - 1; i >= 0; i--) {
                    const vm = matched[i].instances && matched[i].instances.default
                    if (vm && typeof vm.loadAiQuoteDraft === 'function') {
                        vm.loadAiQuoteDraft()
                        return
                    }
                }
            }
            // Create and edit share one component, which the router reuses between them: rebuild it
            const reuse = ['CreateCustomQuote', 'EditCustomQuote'].includes(this.$route.name)
            this.$router.push(target).then(() => {
                if (reuse) this.$store.commit('ai_chat/reload_page')
            }).catch(() => {})
        },
        refreshPage() {
            try {
                sessionStorage.setItem(OPEN_KEY, '1')
            } catch (e) {
                // Storage blocked: the bubble reopens closed
            }
            window.location.reload()
        },
    },
}
</script>

<style lang="scss" scoped>
// Steel blues drawn from the app bar (#4A86B8), deepened for contrast
$accent: #2F6FA3;
$accent-dark: #1F4E79;
$accent-deep: #163A5C;

.ai-chat__launcher {
    position: fixed !important;
    right: 20px;
    bottom: 40px;
    // Above the app bar (Default.vue forces it to 10), below Vuetify dialogs/menus
    z-index: 11;
    background: linear-gradient(135deg, #4A86B8, $accent-dark) !important;
    transition: transform 0.2s ease;

    &:hover {
        transform: scale(1.06);
    }
}

.ai-chat__panel {
    position: fixed;
    right: 20px;
    bottom: 108px;
    z-index: 12;
    width: 520px;
    max-width: calc(100vw - 32px);
    // Leaves room for the ~72px app bar above it
    height: min(660px, calc(100vh - 200px));
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 14px;
    overflow: hidden;

    @media (max-width: 600px) {
        right: 16px;
        left: 16px;
        width: auto;
        max-width: none;
    }
}

.ai-chat__grip {
    position: absolute;
    top: 3px;
    left: 3px;
    z-index: 2;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    cursor: nwse-resize;
    opacity: 0.75;
    transition: opacity 0.15s ease, background 0.15s ease;

    &:hover {
        opacity: 1;
        background: rgba(255, 255, 255, 0.2);
    }

    @media (max-width: 600px) {
        display: none;
    }
}

.ai-chat__history-delete {
    flex-shrink: 0;
    margin-left: auto;
    opacity: 0.5;

    &:hover {
        opacity: 1;
        color: #C62828 !important;
    }
}

.ai-chat__link--danger {
    color: #C62828;
}

.ai-chat__actions {
    display: flex;
    gap: 2px;
    margin-left: 2px;

    // Narrow panel: icons only (each button keeps its title tooltip)
    &--compact .ai-chat__action-label {
        display: none;
    }
}

.ai-chat__action {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    color: #fff;
    background: transparent;
    border: none;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease;

    &:hover:not(:disabled),
    &--active {
        background: rgba(255, 255, 255, 0.2);
    }

    &:disabled {
        opacity: 0.45;
        cursor: default;
    }
}

.ai-chat__dropzone {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 24px;
    background: rgba(244, 248, 252, 0.94);
    border: 2px dashed #5B95C7;
    border-radius: 14px;
    // The overlay must not swallow the drag events that keep the depth count right
    pointer-events: none;

    &--off {
        border-color: #BDBDBD;
        background: rgba(245, 245, 245, 0.94);
    }

    .ai-chat__dropzone-title {
        margin-top: 8px;
        font-size: 15px;
        font-weight: 600;
        color: $accent-deep;
    }

    &--off .ai-chat__dropzone-title {
        color: #757575;
    }

    .ai-chat__dropzone-text {
        margin-top: 4px;
        font-size: 12px;
        color: #757575;
    }
}

.ai-chat__panel--resizing {
    user-select: none;
}

.ai-chat__resize {
    position: absolute;
    z-index: 2;

    &--top {
        top: 0;
        left: 14px;
        right: 0;
        height: 6px;
        cursor: ns-resize;
    }

    &--left {
        top: 14px;
        bottom: 0;
        left: 0;
        width: 6px;
        cursor: ew-resize;
    }

    @media (max-width: 600px) {
        display: none;
    }
}

.ai-chat__history {
    padding: 8px;
}

.ai-chat__history-item {
    display: block;
    width: 100%;
    text-align: left;
    padding: 8px 10px;
    margin-bottom: 6px;
    background: #fff;
    border: 1px solid #DCE8F3;
    border-radius: 8px;
    cursor: pointer;

    &:hover {
        border-color: #9DC0E0;
        background: #F4F8FC;
    }

    &--current {
        border-color: #7FAAD2;
    }
}

.ai-chat__history-title {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    font-weight: 500;
    color: #424242;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ai-chat__history-meta {
    font-size: 11px;
    color: #9E9E9E;
    margin-top: 2px;
}

.ai-chat__history-tag {
    display: inline-block;
    margin-left: 4px;
    padding: 0 6px;
    border-radius: 8px;
    font-size: 10px;
    background: #FFF8E1;
    color: #6D4C41;

    &--current {
        background: #DCE8F3;
        color: $accent;
    }
}

.ai-chat__readonly {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    font-size: 12px;
    color: #616161;
    background: #F5F5F5;
    border-top: 1px solid #E0E0E0;
    flex-shrink: 0;
}

.ai-chat-pop-enter-active,
.ai-chat-pop-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}

.ai-chat-pop-enter,
.ai-chat-pop-leave-to {
    opacity: 0;
    transform: translateY(12px);
}

.ai-chat__header {
    display: flex;
    align-items: center;
    padding: 10px 6px 10px 26px; // left: room for the resize grip
    background: linear-gradient(135deg, #4A86B8, $accent-dark);
    color: #fff;
    flex-shrink: 0;
}

.ai-chat__title {
    flex: 1;
    min-width: 0;
}

.ai-chat__name {
    font-weight: 600;
    font-size: 14px;
    line-height: 1.2;
}

.ai-chat__page {
    font-size: 11px;
    opacity: 0.85;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ai-chat__model {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 2px 7px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.18);
    margin: 0 4px;
    cursor: default;

    &--advanced {
        background: #FFD54F;
        color: #4E342E;
    }
}

.ai-chat__banner {
    display: flex;
    align-items: center;
    padding: 6px 12px;
    font-size: 12px;
    color: #546E7A;
    background: #ECEFF1;
    flex-shrink: 0;

    &--warn {
        color: #8D4B00;
        background: #FFF3E0;
    }
}

.ai-chat__messages {
    flex: 1;
    overflow-y: auto;
    padding: 6px 12px 12px;
    background: #F5F8FB;
}

.ai-chat__empty {
    text-align: center;
    padding: 0 16px 4px;
    color: #757575;

    .ai-chat__empty-title {
        margin-top: 2px;
        font-weight: 600;
        color: #424242;
    }

    .ai-chat__empty-text {
        font-size: 12px;
        margin-top: 4px;
    }
}

.ai-chat__can-do {
    margin: 8px 0 0;
    padding: 8px 12px;
    text-align: left;
    background: #fff;
    border: 1px solid #BCD4EA;
    border-left: 4px solid $accent;
    border-radius: 8px;

    .ai-chat__can-do-title {
        display: flex;
        align-items: center;
        font-size: 14px;
        font-weight: 700;
        color: $accent-deep;
    }

    .ai-chat__can-do-list {
        margin: 4px 0 0;
        padding-left: 20px;
        font-size: 12px;
        line-height: 1.35;
        color: #424242;
    }
}

.ai-chat__mindful {
    display: flex;
    align-items: flex-start;
    margin-top: 8px;
    padding: 6px 12px;
    text-align: left;
    font-size: 12px;
    line-height: 1.45;
    color: #6D4C41;
    background: #FFF8E1;
    border-radius: 8px;

    .v-icon {
        margin-top: 2px;
    }
}

.ai-chat__row {
    display: flex;
    margin-bottom: 10px;

    &--user {
        justify-content: flex-end;

        .ai-chat__msg {
            background: $accent;
            color: #fff;
            border-bottom-right-radius: 4px;
        }
    }

    &--assistant .ai-chat__msg {
        background: #fff;
        border: 1px solid #DCE8F3;
        border-bottom-left-radius: 4px;
    }
}

.ai-chat__msg {
    max-width: 88%;
    padding: 8px 12px;
    border-radius: 14px;
    font-size: 13px;
    line-height: 1.45;
    word-wrap: break-word;
}

.ai-chat__user-text {
    white-space: pre-wrap;
}

.ai-chat__msg-files {
    margin-bottom: 4px;

    .ai-chat__file-pill {
        background: rgba(255, 255, 255, 0.2);
        color: #fff;
    }
}

.ai-chat__msg-tools {
    margin-top: 6px;
    font-size: 11px;
    color: #9E9E9E;
}

.ai-chat__changed {
    margin-top: 6px;
    font-size: 12px;
    color: #2E7D32;
}

.ai-chat__awaiting {
    margin-top: 4px;
    font-size: 12px;
    color: #E65100;
}

.ai-chat__note {
    width: 100%;
    padding: 8px 12px;
    font-size: 12px;
    border-left: 3px solid #7FAAD2;
    background: #E8F1F9;
    border-radius: 6px;
    color: $accent-deep;

    .ai-chat__note-title {
        font-weight: 600;
        margin-bottom: 4px;
    }
}

.ai-chat__md {
    ::v-deep p {
        margin: 0 0 6px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    ::v-deep ul,
    ::v-deep ol {
        margin: 2px 0 6px;
        padding-left: 18px;
    }

    ::v-deep li {
        margin: 1px 0;
    }

    ::v-deep h4,
    ::v-deep h5,
    ::v-deep h6 {
        font-size: 13px;
        margin: 6px 0 4px;
    }

    ::v-deep code {
        background: #E8F1F9;
        padding: 0 4px;
        border-radius: 3px;
        font-size: 12px;
    }

    ::v-deep pre {
        background: #F5F5F5;
        padding: 6px 8px;
        border-radius: 6px;
        overflow-x: auto;
        margin-bottom: 6px;

        code {
            background: none;
            padding: 0;
        }
    }

    ::v-deep .md-table {
        overflow-x: auto;
        margin-bottom: 6px;
    }

    ::v-deep table {
        border-collapse: collapse;
        font-size: 12px;
    }

    ::v-deep th,
    ::v-deep td {
        border: 1px solid #E0E0E0;
        padding: 3px 6px;
        text-align: left;
    }

    ::v-deep th {
        background: #F5F5F5;
    }
}

.ai-chat__progress {
    min-width: 160px;
}

.ai-chat__step {
    font-size: 12px;
    color: #616161;

    &--error {
        color: #EF6C00;
    }

    &--model {
        color: $accent;
    }
}

.ai-chat__typing {
    display: flex;
    gap: 4px;
    padding: 4px 0 0;

    span {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #9DC0E0;
        animation: ai-chat-dot 1.2s infinite ease-in-out;

        &:nth-child(2) {
            animation-delay: 0.15s;
        }

        &:nth-child(3) {
            animation-delay: 0.3s;
        }
    }
}

@keyframes ai-chat-dot {
    0%, 80%, 100% {
        opacity: 0.3;
        transform: translateY(0);
    }
    40% {
        opacity: 1;
        transform: translateY(-3px);
    }
}

.ai-chat__ended {
    padding: 10px 12px;
    background: #FFF8E1;
    border-top: 1px solid #FFE082;
    flex-shrink: 0;

    .ai-chat__ended-text {
        font-size: 12px;
        color: #6D4C41;
        margin-bottom: 8px;
    }
}

.ai-chat__chips {
    padding: 6px 10px 2px;
    border-top: 1px solid #E8F1F9;
    max-height: 120px;
    overflow-y: auto;
    flex-shrink: 0;
}

.ai-chat__chip-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    margin-bottom: 4px;
}

.ai-chat__chips-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #9E9E9E;
    margin-right: 2px;

    &--gap {
        color: #FF8F00;
    }
}

.ai-chat__chip {
    font-size: 12px;

    &--gap {
        font-weight: 600;
        border: 1px solid #FFCA28 !important;
    }
}

.ai-chat__error {
    display: flex;
    align-items: center;
    margin: 0 10px 4px;
    padding: 4px 8px;
    font-size: 12px;
    color: #C62828;
    background: #FFEBEE;
    border-radius: 6px;
    flex-shrink: 0;
}

.ai-chat__composer {
    padding: 4px 8px 8px;
    border-top: 1px solid #DCE8F3;
    flex-shrink: 0;
}

.ai-chat__files {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 4px 4px 0;
}

.ai-chat__file-pill {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    padding: 1px 8px;
    border-radius: 10px;
    background: #E8F1F9;
    color: $accent-dark;
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.ai-chat__file-remove {
    cursor: pointer;
}

.ai-chat__textarea {
    font-size: 13px;

    ::v-deep textarea {
        max-height: 140px;
        overflow-y: auto !important;
    }
}

.ai-chat__link {
    font-size: 12px;
    color: $accent;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        text-decoration: underline;
    }

    &:disabled {
        color: #BDBDBD;
        cursor: default;
    }
}
</style>
